import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { BriefcaseBusiness, ChevronRight, ClipboardList, Plus, ShoppingBag } from "lucide-react";
import { StatusBadge } from "../ui/index.js";
import { PROJECT_STATUS_TONE } from "../catalog/catalogUtils.js";
import { formatPrice } from "../catalog/catalogUtils.js";
import { CrmSection } from "./CrmParts.jsx";
import { formatDate } from "./crmUtils.js";

function Group({ icon: Icon, title, count, empty, children }) {
    return (
        <div className="flex flex-col gap-2">
            <h3 className="flex items-center gap-2 text-sm font-medium text-base-content/80">
                <Icon className="w-4 h-4" aria-hidden="true" /> {title}
                {count > 0 && <span className="text-xs text-base-content/50 tabular-nums">{count}</span>}
            </h3>
            {count === 0 ? <p className="text-xs text-base-content/50">{empty}</p> : <ul className="flex flex-col gap-1">{children}</ul>}
        </div>
    );
}

Group.propTypes = {
    icon: PropTypes.elementType.isRequired,
    title: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
    empty: PropTypes.string.isRequired,
    children: PropTypes.node,
};

/**
 * Lo que tiene con nosotros un lead o un cliente (C3D-46): proyectos, pedidos
 * y solicitudes. `endpoint` es `/leads/<id>/` o `/clients/<id>/`.
 */
export default function CrmActivityPanel({ endpoint, newProjectParam, reloadKey = 0 }) {
    const navigate = useNavigate();
    const [state, setState] = useState({ loading: true, error: false, data: null });

    const load = useCallback(() => {
        setState((prev) => ({ ...prev, loading: true, error: false }));
        axios.get(`${endpoint}activity/`)
            .then(({ data }) => setState({ loading: false, error: false, data }))
            .catch(() => setState({ loading: false, error: true, data: null }));
    }, [endpoint]);
    useEffect(() => { load(); }, [load, reloadKey]);

    const newProject = (
        <button type="button" className="btn btn-primary btn-xs gap-1" onClick={() => navigate(`/admin/proyectos?nuevo=${newProjectParam}`)}>
            <Plus className="w-3.5 h-3.5" aria-hidden="true" /> Proyecto
        </button>
    );

    if (state.loading) {
        return <div className="skeleton h-64 rounded-xl" aria-busy="true" aria-label="Cargando actividad" />;
    }
    if (state.error) {
        return (
            <CrmSection title="Actividad">
                <p role="alert" className="text-sm text-error">No se ha podido cargar.</p>
                <button type="button" className="btn btn-sm self-start" onClick={load}>Reintentar</button>
            </CrmSection>
        );
    }

    const { projects, project_count: projectCount, orders, order_count: orderCount, requests, request_count: requestCount } = state.data;

    return (
        <CrmSection title="Actividad" action={newProject}>
            <Group icon={BriefcaseBusiness} title="Proyectos" count={projectCount} empty="Todavía no tiene proyectos.">
                {projects.map((project) => (
                    <li key={project.id}>
                        <button
                            type="button"
                            onClick={() => navigate(`/admin/proyectos?id=${project.id}`)}
                            className="w-full flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-base-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                        >
                            <span className="min-w-0">
                                <span className="block text-sm truncate">{project.name}</span>
                                <span className="block text-xs text-base-content/60">
                                    {project.item_count} {project.item_count === 1 ? "modelo" : "modelos"}
                                </span>
                            </span>
                            <span className="flex items-center gap-1 shrink-0">
                                <StatusBadge label={project.status_label} tone={PROJECT_STATUS_TONE[project.status]} size="xs" />
                                <ChevronRight className="w-4 h-4 text-base-content/40" aria-hidden="true" />
                            </span>
                        </button>
                    </li>
                ))}
            </Group>

            <Group icon={ShoppingBag} title="Pedidos de la tienda" count={orderCount} empty="Sin pedidos.">
                {orders.map((order) => (
                    <li key={order.id} className="flex items-center justify-between gap-2 px-2 py-1 text-sm">
                        <span className="min-w-0">
                            <span className="block truncate font-mono text-xs">{order.order_number}</span>
                            <span className="block text-xs text-base-content/60">{formatDate(order.created_at)} · {order.status_label}</span>
                        </span>
                        <span className="tabular-nums shrink-0">{formatPrice(order.total_price)}</span>
                    </li>
                ))}
            </Group>

            <Group icon={ClipboardList} title="Solicitudes de pedido" count={requestCount} empty="Sin solicitudes.">
                {requests.map((request) => (
                    <li key={request.id} className="flex items-center justify-between gap-2 px-2 py-1 text-sm">
                        <span className="min-w-0">
                            <span className="block truncate">{request.design_name}</span>
                            <span className="block text-xs text-base-content/60">{request.quantity} uds · {request.commercial_status_label}</span>
                        </span>
                        <span className="text-xs text-base-content/60 shrink-0">{formatDate(request.created_at)}</span>
                    </li>
                ))}
            </Group>
        </CrmSection>
    );
}

CrmActivityPanel.propTypes = {
    endpoint: PropTypes.string.isRequired,
    newProjectParam: PropTypes.string.isRequired,
    reloadKey: PropTypes.number,
};
